<script setup lang="ts">
import { computed, inject } from 'vue'
import { toneOf, TONE_CLASS, type Tone } from '~/utils/state'
import StatusDot from './StatusDot.vue'

/**
 * Dot + tinted status word. Facts after the word stay neutral — pass them in the default slot.
 * label: the entity's name in foreground instead of the word — the dot alone carries the state
 * (endpoint lists where the name is the row's identity).
 */
const props = defineProps<{ status: string; tone?: Tone; label?: string }>()
const tone = computed(() => props.tone ?? toneOf(props.status))
/* Inside a parameter row the facts wrap under the word, right-aligned like every value there; in a
   one-line table cell they are cut with an ellipsis rather than drawn past the cell. */
const inRow = inject<boolean>('param-row', false)
</script>

<template>
  <span class="flex min-w-0 items-center gap-1.5 text-xs" :class="inRow ? 'flex-wrap justify-end gap-y-0.5' : 'whitespace-nowrap'">
    <span class="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap">
      <StatusDot :status="status" :tone="tone" />
      <!-- The word beside the dot is always the state word in its tone; `label` only substitutes the word (Right / Wrong for Ready / Failed). -->
      <span :class="TONE_CLASS[tone].text">{{ label ?? status }}</span>
    </span>
    <span v-if="$slots.default" class="num min-w-0 text-[length:var(--type-meta)] text-muted-foreground" :class="inRow ? 'text-right' : 'truncate'"><slot /></span>
  </span>
</template>
